import clsx from 'clsx';
import { forwardRef, useCallback, useMemo } from 'react';
import { useBasket } from '~/features/basket';
import {
    usePDPActions,
    usePDPData,
    usePDPState,
    usePDPStatus,
} from '~/features/product-details/context';
import { TranslationKey } from '~/lib';
import { SalesforceProductButton } from '~/lib/salesforce/types';
import {
    canDeliverProductToStore,
    extractRemainingStockFromText,
    LOW_STOCK_THRESHOLD,
    StockLabel,
} from '~/lib/salesforce/utilities/product-stock';
import { Button, ButtonProps, Typography } from '~/shared/components';
import { transformSalesforceProductToDataLayer } from '~/shared/hooks/useEvents/helpers';
import { useTranslation } from '~/shared/utils/translation';
import { ProductStockLabel } from './ProductStockLabel';
import styles from './ProductActions.module.scss';

type ActionProps = ButtonProps & {
    button: SalesforceProductButton;
    /** Asks for the shorter label, for places too narrow to fit the full one. */
    condensed?: boolean;
    containerClassName?: string;
    footnote?: React.ReactNode;
    text?: string;
};

/**
 * The <Action /> component should primarily only be used for presentational purposes.
 * All business-logic should be in the <*ProductAction /> components - `shouldRender` included,
 * since the primary action honours it for one of its two buttons and not the other.
 */
const Action: React.FC<ActionProps> = ({
    containerClassName,
    button,
    condensed,
    disabled,
    footnote,
    variant,
    text,
    onClick,
    ...rest
}) => {
    const { translate } = useTranslation();

    /*
     * Condensed labels are written per button as they are needed, so anything without one falls
     * back to its full label rather than rendering empty.
     */
    const condensedText = condensed
        ? translate(`plp.condensed.${button.translationKey}` as TranslationKey)
        : '';

    const buttonText =
        text || condensedText || translate(`plp.${button.translationKey}` as TranslationKey);

    return (
        <div className={containerClassName}>
            <Button variant={variant} {...rest} disabled={disabled} onClick={onClick}>
                {buttonText}
            </Button>

            {footnote && (
                <Typography className={styles.actionFootnote} variant="paragraph-S" as="div">
                    {footnote}
                </Typography>
            )}
        </div>
    );
};

type ProductActionProps = Omit<ActionProps, 'button' | 'disabled' | 'footnote' | 'variant'> & {
    footnote?: boolean;
};

const PrimaryActionFootnote: React.FC = () => {
    const { translate } = useTranslation();
    const { product } = usePDPData();

    const remainingStock = extractRemainingStockFromText(product.c_persuasiveText);

    const lowStockText =
        remainingStock !== undefined && remainingStock < LOW_STOCK_THRESHOLD
            ? translate('pdp.persuasive', { amount: remainingStock.toString() })
            : null;

    if (!product.c_deliveryDaysText && !lowStockText) return null;

    return (
        <>
            {lowStockText && <ProductStockLabel tone="yellow">{lowStockText}</ProductStockLabel>}

            {product.c_deliveryDaysText}
        </>
    );
};

export const PrimaryProductAction: React.FC<ProductActionProps> = ({
    footnote = true,
    condensed,
    onClick,
    ...rest
}) => {
    const { translate } = useTranslation();
    const { product, isSoldOutPermanentlyOnline } = usePDPData();
    const { areProductActionsDisabled } = usePDPStatus();
    const { setIsNotificationDialogOpen } = usePDPActions();
    const { isPrelaunchBlockingBuy } = usePDPState();
    const { isInitialized, addItemToBasket, toggleMiniBasket } = useBasket();

    const primaryButton = product.c_buyButtonsObj?.c_primaryButton;

    const addProductToCart = useCallback(
        (event: React.MouseEvent<HTMLButtonElement & HTMLAnchorElement>) => {
            if (!isInitialized) return;

            const eventData = transformSalesforceProductToDataLayer(product);

            addItemToBasket(
                eventData,
                {
                    productId: product.id,
                    orderable: product.inventory?.orderable,
                },
                1,
            );

            toggleMiniBasket(true);

            onClick?.(event);
        },
        [isInitialized, product.id, onClick],
    );

    const openNotificationDialog = useCallback(
        (event: React.MouseEvent<HTMLButtonElement & HTMLAnchorElement>) => {
            setIsNotificationDialogOpen(true);

            onClick?.(event);
        },
        [onClick],
    );

    // Without a button there is nothing to render and nothing to decide.
    if (!primaryButton) return null;

    const canBuyProduct = primaryButton.shouldRender && !primaryButton.isDisabled;

    const baseActionProps: ActionProps = {
        variant: 'pdp',
        button: primaryButton,
        condensed,
        ...rest,
    };

    if (isPrelaunchBlockingBuy) return <Action {...baseActionProps} disabled />;

    /**
     * If product can't be bought, we do the following:
     * 1. If it's sold out permanently online, we won't provide primary actions.
     * 2. Else, we will allow the user to subscribe to product stock notifications.
     */
    if (!canBuyProduct) {
        if (isSoldOutPermanentlyOnline) return null;

        const isComingSoon =
            primaryButton.translationKey === 'comingsoon' ||
            primaryButton.translationKey === 'comingsoononline';

        const condensedKey = (
            isComingSoon ? 'pdp.condensed.notifyComingSoon' : 'pdp.condensed.notifyDefault'
        ) as TranslationKey;

        const buttonText =
            (condensed ? translate(condensedKey) : '') ||
            translate(isComingSoon ? 'pdp.notifyComingSoon' : 'pdp.notifyDefault');

        // The old banner tracked `isDisabled` alone, so a button that merely opts out of
        // rendering gets the notify CTA without the explanatory line.
        const footnoteText =
            footnote && primaryButton.isDisabled
                ? translate(
                      isComingSoon ? 'pdp.comingsoononlineBanner' : 'pdp.outOfStockOnlineBanner',
                  )
                : undefined;

        return (
            <Action
                {...baseActionProps}
                disabled={areProductActionsDisabled}
                footnote={footnoteText}
                text={buttonText}
                onClick={openNotificationDialog}
            />
        );
    }

    const isDisabled = !isInitialized || areProductActionsDisabled;

    return (
        <Action
            {...baseActionProps}
            disabled={isDisabled}
            footnote={footnote ? <PrimaryActionFootnote /> : null}
            onClick={addProductToCart}
        />
    );
};

export const SecondaryProductAction: React.FC<ProductActionProps> = ({
    footnote = true,
    onClick,
    ...rest
}) => {
    const { translate } = useTranslation();
    const { product } = usePDPData();
    const { areProductActionsDisabled } = usePDPStatus();
    const { setIsReservationDrawerOpen } = usePDPActions();

    const secondaryButton = product.c_buyButtonsObj?.c_secondaryButton;
    const storesWithStock = product.c_storeIdsWithStock?.length ?? 0;

    const handleClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement & HTMLAnchorElement>) => {
            setIsReservationDrawerOpen(true);

            onClick?.(event);
        },
        [onClick],
    );

    const storeStockLabel = useMemo<StockLabel | null>(() => {
        // Product is available in-store
        if (product.c_in_stock_store === true) {
            return { key: 'pdp.overview.inStockStoreLabel', tone: 'green' };
        }

        // Product can be delivered to store
        if (canDeliverProductToStore(product)) {
            return { key: 'plp.deliverToStoreLabel', tone: 'yellow' };
        }

        return null;
    }, [product]);

    const reserveDisabledMessage =
        product.c_isProductClickandReserveDisabled && translate('pdp.reserveDisabledMessage');

    const footnoteContent = useMemo(() => {
        if (!footnote || (!storeStockLabel && !reserveDisabledMessage)) return null;

        return (
            <>
                {storeStockLabel && (
                    <ProductStockLabel tone={storeStockLabel.tone}>
                        {translate(storeStockLabel.key, {
                            amount: storesWithStock.toString(),
                        })}
                    </ProductStockLabel>
                )}

                {reserveDisabledMessage}
            </>
        );
    }, [footnote, storeStockLabel, reserveDisabledMessage]);

    if (!secondaryButton?.shouldRender) return null;

    const isDisabled = secondaryButton.isDisabled || !storesWithStock || areProductActionsDisabled;

    return (
        <Action
            button={secondaryButton}
            footnote={footnoteContent}
            {...rest}
            onClick={handleClick}
            disabled={isDisabled}
            variant="pdp-secondary"
        />
    );
};

export const ProductActions = forwardRef<HTMLDivElement, React.HtmlHTMLAttributes<HTMLDivElement>>(
    ({ className, ...rest }, ref) => {
        // A delivery estimate means nothing while the offer is only announced.
        const { isPrelaunchBlockingBuy } = usePDPState();

        const showFootnotes = !isPrelaunchBlockingBuy;

        return (
            <div ref={ref} className={clsx(styles.buttons, className)} {...rest}>
                <PrimaryProductAction containerClassName={styles.button} footnote={showFootnotes} />
                <SecondaryProductAction
                    containerClassName={styles.button}
                    footnote={showFootnotes}
                />
            </div>
        );
    },
);

ProductActions.displayName = 'ProductActions';
